import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Chip } from "@mui/material";
import { Image } from "antd";
import { useState } from "react";
import Helper from "../../../Helper";
import { WaypointStep } from "../../../models/WaypointStep";
import { mapsIcons } from "../../Helper/FontAwesomeMapsIcons";
import { convertDistanceToReadable } from "../../Helper/WaypointGuidance";

interface WaypointGuidanceStepProps {
    stepIndex: number;
    totalSteps: number;
    waypointStep: WaypointStep;
    setStep: (step: number) => void;
    showPicture: boolean;
    distanceToNextWaypoint: number;
    hasUserLocation: boolean;
}

export default function WaypointGuidanceStep({
    stepIndex,
    totalSteps,
    waypointStep,
    setStep,
    showPicture,
    distanceToNextWaypoint,
    hasUserLocation,
}: WaypointGuidanceStepProps) {
    const [open, setOpen] = useState(false);

    const changeStep = (step: number) => {
        if (step < 1 || step > totalSteps) {
            return;
        }
        setStep(step);
    };

    return (
        <div id={stepIndex.toString()} className="flex flex-col h-full bg-white rounded-xl w-full">
            <div className="p-4 flex items-center justify-between bg-plot-orange">
                <div className="text-white text-base font-semibold">
                    Step {stepIndex} of {totalSteps}
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={() => changeStep(stepIndex - 1)}
                        type="button"
                        disabled={stepIndex === 1}
                        className={Helper.classNames(
                            "flex flex-row items-center gap-2 text-white border border-white rounded-lg p-2 ",
                            stepIndex === 1
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:scale-110 transition-all duration-300 cursor-pointer"
                        )}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        Previous
                    </button>
                    <button
                        onClick={() => changeStep(stepIndex + 1)}
                        type="button"
                        disabled={stepIndex === totalSteps}
                        className={Helper.classNames(
                            "flex flex-row items-center gap-2 text-white border border-white rounded-lg p-2 ",
                            stepIndex === totalSteps
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:scale-110 transition-all duration-300 cursor-pointer"
                        )}
                    >
                        Next
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
            <div className="flex flex-col h-full p-4">
                <div className="flex flex-row items-center gap-2 text-lg leading-tight py-2 w-full justify-between">
                    <div className="flex flex-row items-center gap-2 text-lg leading-tight">
                        {waypointStep.icon && (
                            <FontAwesomeIcon icon={mapsIcons[waypointStep.icon]} color={waypointStep.color} size="lg" />
                        )}
                        <p className="text-plot-slate!">{waypointStep.instruction}</p>
                    </div>

                    <Chip
                        label={convertDistanceToReadable(distanceToNextWaypoint)}
                        color="info"
                        className={Helper.classNames(!hasUserLocation && "invisible")}
                    />
                </div>
                {waypointStep.pictureUrl && showPicture && (
                    <div className="h-full grow min-h-48 rounded-xl relative overflow-hidden waypointImagePreview">
                        <img
                            src={waypointStep.pictureUrl}
                            alt={waypointStep.instruction || stepIndex.toString()}
                            className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
                            onClick={() => {
                                setOpen(true);
                            }}
                        />

                        <Image
                            id="waypointImagePreview"
                            style={{ display: "none" }}
                            alt={waypointStep.instruction || stepIndex.toString()}
                            src={waypointStep.pictureUrl}
                            preview={{
                                visible: open,
                                src: waypointStep.pictureUrl,
                                onVisibleChange: (value) => {
                                    setOpen(value);
                                },
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
